import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Mail, User, MessageSquare, Send, CheckCircle, AlertCircle, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { openCookiePreferences } from '../components/CookieConsent';

const inquiryTypes = [
  { value: 'access', label: 'Request My Data' },
  { value: 'deletion', label: 'Delete My Data' },
  { value: 'correction', label: 'Correct My Data' },
  { value: 'question', label: 'General Question' },
];

const PrivacyPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    inquiry_type: 'question',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const { error } = await supabase
      .from('privacy_inquiries')
      .insert([formData]);

    setIsSubmitting(false);

    if (error) {
      setSubmitStatus('error');
    } else {
      setSubmitStatus('success');
      setFormData({ name: '', email: '', inquiry_type: 'question', message: '' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <section className="pt-32 pb-16 px-6">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent/10 mb-6">
              <Shield className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Your privacy matters. Here's how we handle your data at hiimjoe.com
            </p>
            <p className="text-sm text-text-tertiary mt-2">
              Last updated: January 4, 2026
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="prose prose-lg max-w-none"
          >
            <div className="space-y-10">
              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">1. Introduction</h2>
                <p className="text-text-secondary leading-relaxed">
                  Welcome to hiimjoe.com ("Site"), operated by Joe Hamilton ("we," "us," or "our").
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information
                  when you visit our website. Please read this policy carefully to understand our practices
                  regarding your personal data.
                </p>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">2. Information We Collect</h2>
                <div className="space-y-4 text-text-secondary leading-relaxed">
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-2">Information You Provide</h3>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Contact information (name, email address) when you submit forms</li>
                      <li>Messages and inquiries you send through our contact forms</li>
                      <li>Any other information you voluntarily provide</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-text-primary mb-2">Automatically Collected Information</h3>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Device information (browser type, operating system)</li>
                      <li>Usage data (pages visited, time spent, interactions)</li>
                      <li>IP address and approximate location</li>
                      <li>Cookies and similar tracking technologies</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">3. How We Use Your Information</h2>
                <ul className="list-disc list-inside space-y-2 text-text-secondary leading-relaxed ml-2">
                  <li>To respond to your inquiries and provide support</li>
                  <li>To improve and optimize our website experience</li>
                  <li>To analyze site usage and understand visitor behavior</li>
                  <li>To protect against unauthorized access and maintain security</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">4. Cookies and Tracking</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We use cookies and similar technologies to enhance your browsing experience.
                  These include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-text-secondary leading-relaxed ml-2 mb-4">
                  <li><strong className="text-text-primary">Essential Cookies:</strong> Required for basic site functionality</li>
                  <li><strong className="text-text-primary">Analytics Cookies:</strong> Help us understand how visitors use our site</li>
                  <li><strong className="text-text-primary">Preference Cookies:</strong> Remember your settings and choices</li>
                  <li><strong className="text-text-primary">Marketing Cookies:</strong> Used to deliver relevant content</li>
                </ul>
                <button
                  onClick={openCookiePreferences}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-accent border border-accent/30 hover:bg-accent/10 rounded-lg transition-colors"
                >
                  Manage Cookie Preferences
                </button>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">5. Data Sharing and Disclosure</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  We do not sell your personal information. We may share your data with:
                </p>
                <ul className="list-disc list-inside space-y-2 text-text-secondary leading-relaxed ml-2">
                  <li><strong className="text-text-primary">Service Providers:</strong> Third parties that help us operate our website (hosting, analytics)</li>
                  <li><strong className="text-text-primary">Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong className="text-text-primary">Business Transfers:</strong> In connection with any merger or acquisition</li>
                </ul>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">6. Data Security</h2>
                <p className="text-text-secondary leading-relaxed">
                  We implement appropriate technical and organizational measures to protect your personal
                  information against unauthorized access, alteration, disclosure, or destruction. However,
                  no method of transmission over the Internet is 100% secure, and we cannot guarantee
                  absolute security.
                </p>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">7. Your Rights</h2>
                <p className="text-text-secondary leading-relaxed mb-4">
                  Depending on your location, you may have the following rights:
                </p>
                <ul className="list-disc list-inside space-y-2 text-text-secondary leading-relaxed ml-2">
                  <li><strong className="text-text-primary">Access:</strong> Request a copy of your personal data</li>
                  <li><strong className="text-text-primary">Correction:</strong> Request correction of inaccurate data</li>
                  <li><strong className="text-text-primary">Deletion:</strong> Request deletion of your personal data</li>
                  <li><strong className="text-text-primary">Opt-out:</strong> Opt out of certain data processing activities</li>
                  <li><strong className="text-text-primary">Portability:</strong> Request your data in a portable format</li>
                </ul>
                <p className="text-text-secondary leading-relaxed mt-4">
                  To exercise these rights, please use the Privacy Request form below.
                </p>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">8. Data Retention</h2>
                <p className="text-text-secondary leading-relaxed">
                  We retain your personal information only for as long as necessary to fulfill the purposes
                  outlined in this policy, unless a longer retention period is required by law. Contact form
                  submissions are retained for up to 2 years unless you request earlier deletion.
                </p>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">9. Third-Party Links</h2>
                <p className="text-text-secondary leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the
                  privacy practices of these external sites. We encourage you to review the privacy policies
                  of any third-party sites you visit.
                </p>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">10. Children's Privacy</h2>
                <p className="text-text-secondary leading-relaxed">
                  Our website is not intended for children under 13 years of age. We do not knowingly collect
                  personal information from children. If you believe we have collected information from a child,
                  please contact us immediately.
                </p>
              </section>

              <section className="bg-surface border border-border rounded-2xl p-6 md:p-8">
                <h2 className="text-xl font-semibold text-text-primary mb-4">11. Changes to This Policy</h2>
                <p className="text-text-secondary leading-relaxed">
                  We may update this Privacy Policy from time to time. The updated version will be indicated
                  by an updated "Last Updated" date. We encourage you to review this policy periodically for
                  any changes.
                </p>
              </section>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-16"
          >
            <div className="bg-surface border border-border rounded-2xl p-6 md:p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-text-primary mb-2">Privacy Request</h2>
                <p className="text-text-secondary">
                  Have a question or want to exercise your privacy rights? Submit a request below.
                </p>
              </div>

              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                    <CheckCircle className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-xl font-semibold text-text-primary mb-2">Request Submitted</h3>
                  <p className="text-text-secondary mb-6">
                    We'll review your request and respond within 30 days.
                  </p>
                  <button
                    onClick={() => setSubmitStatus('idle')}
                    className="text-sm text-accent hover:text-teal-bright transition-colors"
                  >
                    Submit another request
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Your Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full pl-11 pr-4 py-3 bg-background border border-border rounded-xl text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-alt focus:ring-1 focus:ring-accent-alt/30 transition-colors"
                          placeholder="John Doe"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary" />
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full pl-11 pr-4 py-3 bg-background border border-border rounded-xl text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-alt focus:ring-1 focus:ring-accent-alt/30 transition-colors"
                          placeholder="john@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Request Type
                    </label>
                    <div className="relative">
                      <select
                        value={formData.inquiry_type}
                        onChange={(e) => setFormData({ ...formData, inquiry_type: e.target.value })}
                        className="w-full px-4 py-3 bg-background border border-border rounded-xl text-text-primary focus:outline-none focus:border-accent-alt focus:ring-1 focus:ring-accent-alt/30 transition-colors appearance-none cursor-pointer"
                      >
                        {inquiryTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-tertiary pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-2">
                      Details
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-text-tertiary" />
                      <textarea
                        required
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full pl-11 pr-4 py-3 bg-background border border-border rounded-xl text-text-primary placeholder:text-text-tertiary focus:outline-none focus:border-accent-alt focus:ring-1 focus:ring-accent-alt/30 transition-colors resize-none"
                        placeholder="Please provide details about your request..."
                      />
                    </div>
                  </div>

                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                      <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-500">
                        Something went wrong. Please try again or email us directly.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 text-base font-medium text-background bg-accent hover:bg-teal-bright rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Submit Request
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 text-center"
          >
            <p className="text-text-secondary">
              For urgent privacy matters, contact us directly at{' '}
              <a
                href="mailto:j.hamilton@servetdigital.com"
                className="text-accent hover:text-teal-bright transition-colors"
              >
                j.hamilton@servetdigital.com
              </a>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
